import { useState, useEffect, useCallback } from 'react'
import {
  Box,
  VStack,
  HStack,
  Text,
  Button,
  IconButton,
  Badge,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Switch,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Input,
  Select,
  Textarea,
  useDisclosure,
  useToast,
  Spinner,
  Flex,
  Heading,
  Card,
  CardBody,
  useColorModeValue,
  Progress,
} from '@chakra-ui/react'
import {
  Plus,
  PencilSimple,
  Trash,
  Robot,
  Clock,
  Warning,
  XCircle,
  ChatText,
  Play,
} from '@phosphor-icons/react'
import api from '../services/api'

interface AutomationRule {
  id: number
  name: string
  type: 'protocol_message' | 'inactivity_warning' | 'inactivity_close' | 'agent_delay_message'
  triggerMinutes: number
  message: string
  categoryId: number | null
  categoryName: string | null
  typeLabel: string
  isActive: boolean
  createdAt: string
  updatedAt: string
}

interface Category {
  id: number
  name: string
}

const AUTOMATION_TYPES = [
  {
    value: 'protocol_message',
    label: 'Mensagem de Protocolo',
    description: 'Enviada ao iniciar o atendimento com o número do protocolo',
    icon: ChatText,
    color: 'blue',
  },
  {
    value: 'inactivity_warning',
    label: 'Aviso de Inatividade',
    description: 'Avisa o usuário que o chat será encerrado por inatividade',
    icon: Clock,
    color: 'orange',
  },
  {
    value: 'inactivity_close',
    label: 'Encerramento por Inatividade',
    description: 'Encerra o chat automaticamente por inatividade do usuário',
    icon: XCircle,
    color: 'red',
  },
  {
    value: 'agent_delay_message',
    label: 'Mensagem de Demora',
    description: 'Enviada se o atendente demorar para responder',
    icon: Warning,
    color: 'yellow',
  },
]

export default function Automations() {
  const [rules, setRules] = useState<AutomationRule[]>([])
  const [categories, setCategories] = useState<Category[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [editingRule, setEditingRule] = useState<AutomationRule | null>(null)
  const surfaceBg = useColorModeValue('white', 'gray.800')
  const subtleBg = useColorModeValue('gray.100', 'gray.700')
  const borderColor = useColorModeValue('gray.200', 'gray.700')
  const borderSubtle = useColorModeValue('gray.300', 'gray.600')
  const textPrimary = useColorModeValue('gray.800', 'white')
  const textSecondary = useColorModeValue('gray.600', 'gray.300')
  const textMuted = useColorModeValue('gray.500', 'gray.400')
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    type: 'protocol_message',
    triggerMinutes: 5,
    message: '',
    categoryId: '',
    isActive: true,
  })
  
  const { isOpen, onOpen, onClose } = useDisclosure()
  const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure()
  const {
    isOpen: isToggleOpen,
    onOpen: onToggleOpen,
    onClose: onToggleClose,
  } = useDisclosure()
  const [deleteTarget, setDeleteTarget] = useState<AutomationRule | null>(null)
  const [testChatId, setTestChatId] = useState('')
  const [isTesting, setIsTesting] = useState(false)
  const [toggleTargetRule, setToggleTargetRule] = useState<AutomationRule | null>(null)
  const [togglePreviewLoading, setTogglePreviewLoading] = useState(false)
  const [togglePreview, setTogglePreview] = useState<{
    chatIds: number[]
    rule: {
      name: string
      typeLabel: string
      categoryName: string | null
    }
  } | null>(null)
  const [togglePhase, setTogglePhase] = useState<'idle' | 'preview' | 'running'>('idle')
  const [toggleProcessed, setToggleProcessed] = useState(0)
  const [toggleErrors, setToggleErrors] = useState(0)
  const [deactivatingRuleId, setDeactivatingRuleId] = useState<number | null>(null)
  const toast = useToast()

  const fetchRules = useCallback(
    async (opts?: { showLoading?: boolean }) => {
      const showLoading = opts?.showLoading !== false
      if (showLoading) setIsLoading(true)
      try {
        const response = await api.get('/admin/automations')
        setRules(response.data.data || [])
      } catch (error) {
        console.error('Error fetching rules:', error)
        toast({
          title: 'Erro ao carregar regras',
          status: 'error',
          duration: 3000,
        })
      } finally {
        if (showLoading) setIsLoading(false)
      }
    },
    [toast]
  )

  const fetchCategories = useCallback(async () => {
    try {
      const response = await api.get('/admin/automations/categories')
      setCategories(response.data.data || [])
    } catch (error) {
      console.error('Error fetching categories:', error)
    }
  }, [])

  useEffect(() => {
    fetchRules()
    fetchCategories()
  }, [fetchRules, fetchCategories])

  const handleOpenCreate = () => {
    setEditingRule(null)
    setFormData({
      name: '',
      type: 'protocol_message',
      triggerMinutes: 5,
      message: '',
      categoryId: '',
      isActive: true,
    })
    onOpen()
  }

  const handleOpenEdit = (rule: AutomationRule) => {
    setEditingRule(rule)
    setFormData({
      name: rule.name,
      type: rule.type,
      triggerMinutes: rule.triggerMinutes,
      message: rule.message,
      categoryId: rule.categoryId?.toString() || '',
      isActive: rule.isActive,
    })
    onOpen()
  }

  const handleSubmit = async () => {
    if (!formData.name.trim()) {
      toast({ title: 'Nome é obrigatório', status: 'warning', duration: 3000 })
      return
    }
    if (!formData.message.trim()) {
      toast({ title: 'Mensagem é obrigatória', status: 'warning', duration: 3000 })
      return
    }

    setIsSubmitting(true)
    try {
      const payload = {
        ...formData,
        categoryId: formData.categoryId ? Number(formData.categoryId) : null,
      }

      if (editingRule) {
        await api.put(`/admin/automations/${editingRule.id}`, payload)
        toast({ title: 'Regra atualizada', status: 'success', duration: 3000 })
      } else {
        await api.post('/admin/automations', payload)
        toast({ title: 'Regra criada', status: 'success', duration: 3000 })
      }

      onClose()
      fetchRules()
    } catch (error: any) {
      toast({
        title: 'Erro ao salvar regra',
        description: error.response?.data?.error || 'Tente novamente',
        status: 'error',
        duration: 5000,
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  const resetToggleModal = () => {
    setToggleTargetRule(null)
    setTogglePreview(null)
    setTogglePhase('idle')
    setToggleProcessed(0)
    setToggleErrors(0)
    setTogglePreviewLoading(false)
  }

  const closeToggleModal = () => {
    if (togglePhase === 'running') return
    resetToggleModal()
    onToggleClose()
  }

  /** Desativar: só persiste o status; checagens em lote são necessárias ao ativar. */
  const handleQuickDeactivate = async (rule: AutomationRule) => {
    setDeactivatingRuleId(rule.id)
    try {
      await api.post(`/admin/automations/${rule.id}/toggle`)
      toast({ title: 'Regra desativada', status: 'success', duration: 2500 })
      await fetchRules({ showLoading: false })
    } catch {
      toast({ title: 'Erro ao desativar', status: 'error', duration: 3000 })
    } finally {
      setDeactivatingRuleId(null)
    }
  }

  const openToggleFlow = async (rule: AutomationRule) => {
    setToggleTargetRule(rule)
    setTogglePreview(null)
    setTogglePhase('preview')
    setToggleProcessed(0)
    setToggleErrors(0)
    onToggleOpen()
    setTogglePreviewLoading(true)
    try {
      const { data } = await api.get(`/admin/automations/${rule.id}/toggle-preview`)
      const chatIds = Array.isArray(data.chatIds)
        ? data.chatIds.map((n: number) => Number(n)).filter((n: number) => Number.isFinite(n))
        : []
      setTogglePreview({
        chatIds,
        rule: {
          name: data.rule?.name ?? rule.name,
          typeLabel: data.rule?.typeLabel ?? rule.typeLabel,
          categoryName: data.rule?.categoryName ?? rule.categoryName,
        },
      })
    } catch {
      toast({
        title: 'Erro ao carregar pré-visualização',
        status: 'error',
        duration: 4000,
      })
      resetToggleModal()
      onToggleClose()
    } finally {
      setTogglePreviewLoading(false)
    }
  }

  const confirmToggleAndReconcile = async () => {
    if (!toggleTargetRule || !togglePreview) return

    setTogglePhase('running')
    setToggleProcessed(0)
    setToggleErrors(0)

    const chatIds = togglePreview.chatIds
    const total = chatIds.length

    try {
      if (total === 0) {
        await api.post(`/admin/automations/${toggleTargetRule.id}/toggle`)
        toast({
          title: 'Regra ativada',
          status: 'success',
          duration: 3000,
        })
      } else {
        const batchSize = 10
        let errorsSum = 0

        for (let i = 0; i < chatIds.length; i += batchSize) {
          const chunk = chatIds.slice(i, i + batchSize)
          const { data } = await api.post(
            `/admin/automations/${toggleTargetRule.id}/reconcile-by-ids`,
            { chatIds: chunk }
          )
          errorsSum += data.errors ?? 0
          setToggleProcessed(Math.min(i + chunk.length, total))
          setToggleErrors(errorsSum)
          await new Promise((r) => requestAnimationFrame(() => r(null)))
        }

        await api.post(`/admin/automations/${toggleTargetRule.id}/toggle`)

        toast({
          title: 'Regra ativada',
          description:
            errorsSum > 0
              ? `${total} atendimento(s) percorridos, ${errorsSum} com aviso no log.`
              : `${total} atendimento(s) verificados em lotes de ${batchSize}.`,
          status: errorsSum > 0 ? 'warning' : 'success',
          duration: 5000,
        })
      }
    } catch (error: any) {
      toast({
        title: 'Erro ao aplicar alteração',
        description: error.response?.data?.error || 'Tente novamente',
        status: 'error',
        duration: 5000,
      })
    } finally {
      resetToggleModal()
      onToggleClose()
    }

    try {
      await fetchRules({ showLoading: false })
    } catch {
      /* lista já atualizada ou erro silencioso */
    }
  }

  const handleDelete = async () => {
    if (!deleteTarget) return
    
    try {
      await api.delete(`/admin/automations/${deleteTarget.id}`)
      toast({ title: 'Regra excluída', status: 'success', duration: 3000 })
      onDeleteClose()
      fetchRules()
    } catch (error) {
      toast({
        title: 'Erro ao excluir regra',
        status: 'error',
        duration: 3000,
      })
    }
  }

  const openDeleteModal = (rule: AutomationRule) => {
    setDeleteTarget(rule)
    onDeleteOpen()
  }

  const handleTestAutomation = async () => {
    if (!testChatId.trim()) {
      toast({ title: 'Digite o ID do chat', status: 'warning', duration: 3000 })
      return
    }

    setIsTesting(true)
    try {
      const response = await api.post(`/admin/automations/test/${testChatId}`)
      toast({
        title: 'Teste executado',
        description: `${response.data.rulesFound} regras encontradas. Verifique o chat.`,
        status: 'success',
        duration: 5000,
      })
      console.log('Test result:', response.data)
    } catch (error: any) {
      toast({
        title: 'Erro no teste',
        description: error.response?.data?.error || 'Tente novamente',
        status: 'error',
        duration: 5000,
      })
    } finally {
      setIsTesting(false)
    }
  }

  const getTypeIcon = (type: string) => {
    const typeConfig = AUTOMATION_TYPES.find((t) => t.value === type)
    if (!typeConfig) return null
    const Icon = typeConfig.icon
    return <Icon size={20} />
  }

  const getTypeColor = (type: string) => {
    const typeConfig = AUTOMATION_TYPES.find((t) => t.value === type)
    return typeConfig?.color || 'gray'
  }

  if (isLoading) {
    return (
      <Flex h="50vh" align="center" justify="center">
        <Spinner size="xl" color="yellow.500" />
      </Flex>
    )
  }

  return (
    <Box p={6}>
      <VStack align="stretch" spacing={6}>
        {/* Header */}
        <HStack justify="space-between">
          <VStack align="start" spacing={1}>
            <Heading size="lg" color={textPrimary}>
              <HStack spacing={2}>
                <Robot size={28} weight="duotone" />
                <Text>Automações</Text>
              </HStack>
            </Heading>
            <Text color={textMuted}>
              Configure mensagens automáticas e regras de inatividade
            </Text>
          </VStack>
          <Button
            leftIcon={<Plus size={18} />}
            bg="yellow.500"
            color="gray.900"
            _hover={{ bg: 'yellow.400' }}
            onClick={handleOpenCreate}
          >
            Nova Automação
          </Button>
        </HStack>

        {/* Types explanation */}
        <Box>
          <Text fontSize="sm" color={textMuted} mb={3}>
            Tipos de automação disponíveis:
          </Text>
          <HStack spacing={4} flexWrap="wrap" gap={3}>
            {AUTOMATION_TYPES.map((type) => {
              const Icon = type.icon
              return (
                <Card key={type.value} bg={subtleBg} size="sm">
                  <CardBody py={2} px={3}>
                    <HStack spacing={2}>
                      <Badge colorScheme={type.color} p={1} borderRadius="md">
                        <Icon size={16} />
                      </Badge>
                      <VStack align="start" spacing={0}>
                        <Text fontSize="xs" color={textPrimary} fontWeight="semibold">
                          {type.label}
                        </Text>
                        <Text fontSize="xs" color={textMuted}>
                          {type.description}
                        </Text>
                      </VStack>
                    </HStack>
                  </CardBody>
                </Card>
              )
            })}
          </HStack>
        </Box>

        {/* Test automation card */}
        <Card bg={subtleBg} size="sm">
          <CardBody>
            <HStack spacing={4}>
              <VStack align="start" spacing={1} flex={1}>
                <Text fontSize="sm" color={textPrimary} fontWeight="semibold">
                  Testar Automações
                </Text>
                <Text fontSize="xs" color={textMuted}>
                  Execute as automações para um chat específico
                </Text>
              </VStack>
              <HStack spacing={2}>
                <Input
                  placeholder="ID do chat"
                  value={testChatId}
                  onChange={(e) => setTestChatId(e.target.value)}
                  w="120px"
                  size="sm"
                  bg={surfaceBg}
                  borderColor={borderSubtle}
                  color={textPrimary}
                />
                <Button
                  leftIcon={<Play size={16} />}
                  size="sm"
                  colorScheme="yellow"
                  onClick={handleTestAutomation}
                  isLoading={isTesting}
                >
                  Testar
                </Button>
              </HStack>
            </HStack>
          </CardBody>
        </Card>

        {/* Rules table */}
        {rules.length === 0 ? (
          <Box textAlign="center" py={12} bg={subtleBg} borderRadius="lg">
            <Robot size={48} color="#A0AEC0" style={{ margin: '0 auto' }} />
            <Text color={textMuted} mt={4}>
              Nenhuma automação configurada
            </Text>
            <Button
              mt={4}
              leftIcon={<Plus size={18} />}
              colorScheme="yellow"
              variant="outline"
              onClick={handleOpenCreate}
            >
              Criar primeira automação
            </Button>
          </Box>
        ) : (
          <Box bg={subtleBg} borderRadius="lg" overflowX="auto">
            <Table variant="simple">
              <Thead>
                <Tr>
                  <Th color={textMuted}>Nome</Th>
                  <Th color={textMuted}>Tipo</Th>
                  <Th color={textMuted}>Tempo</Th>
                  <Th color={textMuted}>Categoria</Th>
                  <Th color={textMuted}>Status</Th>
                  <Th color={textMuted} textAlign="right">Ações</Th>
                </Tr>
              </Thead>
              <Tbody>
                {rules.map((rule) => (
                  <Tr key={rule.id} _hover={{ bg: 'gray.650' }}>
                    <Td color={textPrimary} fontWeight="medium">
                      {rule.name}
                    </Td>
                    <Td>
                      <Badge
                        colorScheme={getTypeColor(rule.type)}
                        display="flex"
                        alignItems="center"
                        gap={1}
                        px={2}
                        py={1}
                      >
                        {getTypeIcon(rule.type)}
                        {rule.typeLabel}
                      </Badge>
                    </Td>
                    <Td color={textSecondary}>
                      {rule.type === 'protocol_message' ? '-' : `${rule.triggerMinutes} min`}
                    </Td>
                    <Td color={textSecondary}>
                      {rule.categoryName || 'Todas'}
                    </Td>
                    <Td>
                      <Switch
                        isChecked={rule.isActive}
                        onChange={() =>
                          rule.isActive ? handleQuickDeactivate(rule) : openToggleFlow(rule)
                        }
                        colorScheme="yellow"
                        isDisabled={deactivatingRuleId === rule.id}
                      />
                    </Td>
                    <Td>
                      <HStack justify="flex-end" spacing={1}>
                        <IconButton
                          aria-label="Editar"
                          icon={<PencilSimple size={18} />}
                          size="sm"
                          variant="ghost"
                          colorScheme="blue"
                          onClick={() => handleOpenEdit(rule)}
                        />
                        <IconButton
                          aria-label="Excluir"
                          icon={<Trash size={18} />}
                          size="sm"
                          variant="ghost"
                          colorScheme="red"
                          onClick={() => openDeleteModal(rule)}
                        />
                      </HStack>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </Box>
        )}
      </VStack>

      {/* Create/Edit Modal */}
      <Modal isOpen={isOpen} onClose={onClose} size="xl" isCentered>
        <ModalOverlay />
        <ModalContent bg={surfaceBg} borderColor={borderColor}>
          <ModalHeader color={textPrimary}>
            {editingRule ? 'Editar Automação' : 'Nova Automação'}
          </ModalHeader>
          <ModalCloseButton color={textMuted} />
          <ModalBody>
            <VStack spacing={4}>
              <FormControl isRequired>
                <FormLabel color={textSecondary}>Nome</FormLabel>
                <Input
                  placeholder="Ex: Aviso de inatividade padrão"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  bg={subtleBg}
                  borderColor={borderSubtle}
                  color={textPrimary}
                />
              </FormControl>

              <FormControl isRequired>
                <FormLabel color={textSecondary}>Tipo</FormLabel>
                <Select
                  value={formData.type}
                  onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                  bg={subtleBg}
                  borderColor={borderSubtle}
                  color={textPrimary}
                >
                  {AUTOMATION_TYPES.map((type) => (
                    <option key={type.value} value={type.value} style={{ background: '#2D3748' }}>
                      {type.label}
                    </option>
                  ))}
                </Select>
              </FormControl>

              {formData.type !== 'protocol_message' && (
                <FormControl isRequired>
                  <FormLabel color={textSecondary}>Tempo (minutos)</FormLabel>
                  <Input
                    type="number"
                    min={1}
                    value={formData.triggerMinutes}
                    onChange={(e) =>
                      setFormData({ ...formData, triggerMinutes: Number(e.target.value) })
                    }
                    bg={subtleBg}
                    borderColor={borderSubtle}
                    color={textPrimary}
                  />
                </FormControl>
              )}

              <FormControl>
                <FormLabel color={textSecondary}>Categoria (opcional)</FormLabel>
                <Select
                  placeholder="Todas as categorias"
                  value={formData.categoryId}
                  onChange={(e) => setFormData({ ...formData, categoryId: e.target.value })}
                  bg={subtleBg}
                  borderColor={borderSubtle}
                  color={textPrimary}
                >
                  {categories.map((cat) => (
                    <option key={cat.id} value={cat.id} style={{ background: '#2D3748' }}>
                      {cat.name}
                    </option>
                  ))}
                </Select>
              </FormControl>

              <FormControl isRequired>
                <FormLabel color={textSecondary}>Mensagem</FormLabel>
                <Textarea
                  placeholder={getMessagePlaceholder(formData.type)}
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  bg={subtleBg}
                  borderColor={borderSubtle}
                  color={textPrimary}
                  rows={4}
                />
                <Text fontSize="xs" color={textMuted} mt={1}>
                  Variáveis disponíveis: {'{protocol}'}, {'{userName}'}, {'{agentName}'}
                </Text>
              </FormControl>

              <FormControl display="flex" alignItems="center">
                <FormLabel color={textSecondary} mb={0}>
                  Ativo
                </FormLabel>
                <Switch
                  isChecked={formData.isActive}
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                  colorScheme="yellow"
                />
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose}>
              Cancelar
            </Button>
            <Button
              bg="yellow.500"
              color="gray.900"
              _hover={{ bg: 'yellow.400' }}
              onClick={handleSubmit}
              isLoading={isSubmitting}
            >
              {editingRule ? 'Salvar' : 'Criar'}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Ativar automação: modal + reconciliação em lotes (desativar é imediato na tabela) */}
      <Modal
        isOpen={isToggleOpen}
        onClose={closeToggleModal}
        isCentered
        closeOnOverlayClick={togglePhase !== 'running'}
        closeOnEsc={togglePhase !== 'running'}
      >
        <ModalOverlay />
        <ModalContent bg={surfaceBg} borderColor={borderColor}>
          <ModalHeader color={textPrimary}>Ativar automação</ModalHeader>
          <ModalCloseButton color={textMuted} isDisabled={togglePhase === 'running'} />
          <ModalBody>
            {togglePreviewLoading && (
              <Flex justify="center" py={8}>
                <Spinner color="yellow.500" />
              </Flex>
            )}
            {!togglePreviewLoading && togglePreview && toggleTargetRule && (
              <VStack align="stretch" spacing={4}>
                <Text color={textSecondary}>
                  <Text as="span" fontWeight="semibold" color={textPrimary}>
                    {togglePreview.rule.name}
                  </Text>{' '}
                  ({togglePreview.rule.typeLabel}
                  {togglePreview.rule.categoryName
                    ? ` · ${togglePreview.rule.categoryName}`
                    : ' · Todas as categorias'}
                  )
                </Text>
                {togglePreview.chatIds.length > 0 ? (
                  <Text color={textSecondary}>
                    Total:{' '}
                    <Text as="span" fontWeight="bold" color={textPrimary}>
                      {togglePreview.chatIds.length}
                    </Text>{' '}
                    atendimento(s) em andamento. Processamento em lotes de{' '}
                    <Text as="span" fontWeight="semibold">
                      10
                    </Text>
                    ; em seguida a regra será ativada. A automação só age quando os critérios forem
                    atendidos (ex.: tempo de inatividade já ultrapassado).
                  </Text>
                ) : (
                  <Text color={textMuted}>
                    Nenhum atendimento em andamento neste escopo. Apenas o status da regra será
                    alterado.
                  </Text>
                )}
                {togglePhase === 'running' && togglePreview.chatIds.length > 0 && (
                  <Box>
                    <Text fontSize="sm" color={textMuted} mb={1}>
                      Processados {Math.min(toggleProcessed, togglePreview.chatIds.length)} de{' '}
                      {togglePreview.chatIds.length}
                      {toggleErrors > 0 ? ` · ${toggleErrors} aviso(s)` : ''}
                    </Text>
                    <Progress
                      size="sm"
                      colorScheme="yellow"
                      borderRadius="md"
                      isIndeterminate={
                        toggleProcessed === 0 && togglePreview.chatIds.length > 0
                      }
                      value={
                        togglePreview.chatIds.length > 0
                          ? (toggleProcessed / togglePreview.chatIds.length) * 100
                          : 0
                      }
                    />
                  </Box>
                )}
              </VStack>
            )}
          </ModalBody>
          <ModalFooter>
            <Button
              variant="ghost"
              mr={3}
              onClick={closeToggleModal}
              isDisabled={togglePhase === 'running'}
            >
              Cancelar
            </Button>
            <Button
              bg="yellow.500"
              color="gray.900"
              _hover={{ bg: 'yellow.400' }}
              onClick={confirmToggleAndReconcile}
              isLoading={togglePhase === 'running'}
              isDisabled={togglePreviewLoading || !togglePreview}
            >
              Confirmar
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Delete Modal */}
      <Modal isOpen={isDeleteOpen} onClose={onDeleteClose} isCentered>
        <ModalOverlay />
        <ModalContent bg={surfaceBg} borderColor={borderColor}>
          <ModalHeader color={textPrimary}>Excluir Automação</ModalHeader>
          <ModalCloseButton color={textMuted} />
          <ModalBody>
            <Text color={textSecondary}>
              Tem certeza que deseja excluir a automação "{deleteTarget?.name}"?
            </Text>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onDeleteClose}>
              Cancelar
            </Button>
            <Button colorScheme="red" onClick={handleDelete}>
              Excluir
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  )
}

function getMessagePlaceholder(type: string): string {
  switch (type) {
    case 'protocol_message':
      return 'Olá {userName}! Seu protocolo de atendimento é {protocol}. Em breve um de nossos atendentes irá te ajudar.'
    case 'inactivity_warning':
      return 'Olá {userName}, você ainda está aí? Se não responder em alguns minutos, o atendimento será encerrado por inatividade.'
    case 'inactivity_close':
      return 'Atendimento encerrado por inatividade. Caso precise de ajuda novamente, por favor abra um novo chamado.'
    case 'agent_delay_message':
      return 'Um momento, {userName}. Estou verificando sua solicitação e logo retorno com uma resposta.'
    default:
      return 'Digite a mensagem que será enviada automaticamente...'
  }
}
